import { useContext, useEffect, useState, useMemo } from 'react'
import GlobalContext from '../../../context/global-context'
import clsx from 'clsx'

// MUI
import { Backdrop } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'


// Components
import { HeadingTitle, Text } from '../../../components/modules/ui'
import InfoModal from './InfoModal'
import Table from './Table'
import CustomButton from '../../../components/modules/ui/CustomButton'

// Constantes de fecha
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'
import businessApi from '../../../api/business.api'

// Hooks
import { useInfiniteLibroDiario } from '../../../hooks/useInfiniteLibroDiario'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import Lottie from 'lottie-react'

const ListLibroDiario = () => {
  const { currentBusiness } = useContext(GlobalContext)

  // estados para el InfoModal
  const [infoOpen, setInfoOpen] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // Obtenemos primer y último día del mes actual
  const today = new Date()
  const mesActual = today.getMonth() + 1
  const añoActual = today.getFullYear()

  // Primer día = "YYYY-MM-01"
  const primerDiaMes = `${añoActual}-${String(mesActual).padStart(2, '0')}-01`

  // Último día: primer día mes siguiente menos 1 día
  const primerDiaMesSiguiente = new Date(añoActual, mesActual, 1)
  const ultimoDiaDate = new Date(primerDiaMesSiguiente.getTime() - 24 * 60 * 60 * 1000)
  const ultimoDiaMes = `${ultimoDiaDate.getFullYear()}-${String(
    ultimoDiaDate.getMonth() + 1
  ).padStart(2, '0')}-${String(ultimoDiaDate.getDate()).padStart(2, '0')}`

  // Estados para el rango de fechas: default = mes completo
  const [queryFrom, setQueryFrom] = useState(primerDiaMes)
  const [queryTo, setQueryTo] = useState(ultimoDiaMes)

  // 2) Estados para los filtros de la tabla
  const [tipoFilter, setTipoFilter] = useState('')
  const [comprobanteFilter, setComprobanteFilter] = useState('')
  const [clienteFilter, setClienteFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Debounced search for better performance
  const [debouncedComprobanteFilter, setDebouncedComprobanteFilter] = useState('')
  const [debouncedClienteFilter, setDebouncedClienteFilter] = useState('')

  // Infinite scroll hook
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteLibroDiario({
    businessId: currentBusiness?.id || 0,
    queryFrom,
    queryTo,
    limit: 30,
    filters: {
      searchQuery: '',
      tipoFilter,
      comprobanteFilter: debouncedComprobanteFilter,
      clienteFilter: debouncedClienteFilter
    }
  })

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  })

  // Debounce effect for search filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedComprobanteFilter(comprobanteFilter)
    }, 300)
    return () => clearTimeout(timer)
  }, [comprobanteFilter])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClienteFilter(clienteFilter)
    }, 300)
    return () => clearTimeout(timer)
  }, [clienteFilter])

  // Flatten all pages data
  const data = useMemo(() => {
    return infiniteData?.pages?.flatMap(page => page.data) || []
  }, [infiniteData])

  const handleFromChange = e => {
    const newFrom = e.target.value
    setQueryFrom(newFrom)
    // Si el nuevo “Desde” es mayor que el “Hasta” actual, igualamos “Hasta” a “Desde”
    if (newFrom > queryTo) {
      setQueryTo(newFrom)
    }
  }

  const handleToChange = e => {
    const newTo = e.target.value
    // Si el usuario eligió un “Hasta” anterior al “Desde”, forzamos a no bajar de “Desde”
    if (newTo < queryFrom) {
      setQueryTo(queryFrom)
    } else {
      setQueryTo(newTo)
    }
  }

  const handleSearch = () => {
    refetch()
  }

  const handleExport = async (email) => {
    setIsExporting(true)
    try {
      await businessApi.exportLibroDiario({
        organizationId: currentBusiness.organizationId,
        businessId: currentBusiness.id,
        email,
        queryFrom,
        queryTo,
        searchQuery: debouncedComprobanteFilter || debouncedClienteFilter || undefined,
        transactionType: tipoFilter || undefined
      })
    } catch (error) {
      console.error('Error al exportar:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  const filteredData = useMemo(() => {
    return (
      data
        // FILTRO por Tipo de Comprobante
        .filter(row => {
          if (!tipoFilter) return true
          return row.tipoComprobante?.name === tipoFilter
        })
        // FILTRO por Comprobante (texto parcial, case-insensitive)
        .filter(row => {
          if (!debouncedComprobanteFilter) return true
          const comprobante = row.comprobante || row.idOrigin || ''
          return comprobante
            .toString()
            .toLowerCase()
            .includes(debouncedComprobanteFilter.trim().toLowerCase())
        })
        // FILTRO por RUT o Razón Social (texto parcial)
        .filter(row => {
          if (!debouncedClienteFilter) return true
          const term = debouncedClienteFilter.trim().toLowerCase()
          const rut = row.informacion?.rutEmpresa?.toString().toLowerCase() || ''
          const razon = row.informacion?.razonSocial?.toLowerCase() || ''
          return rut.includes(term) || razon.includes(term)
        })
    )
  }, [data, tipoFilter, debouncedComprobanteFilter, debouncedClienteFilter])

  const [showModal, setShowModal] = useState({
    show: false,
    title: '',
    body: null
  })
  const Modal = () => {
    const { show, title, body } = showModal
    if (!show) return null
    return (
      <Backdrop sx={{ zIndex: 1600 }} open={true}>
        <div className='py-6 px-12 flex flex-col items-center justify-center bg-white'>
          <Text variant='h6' className='text-xl mb-4 text-center'>
            {title}
          </Text>
          {body}
          <button
            className='bg-primary text-white rounded-full px-4 py-2 hover:bg-primary/80 focus:outline-none shadow mt-4'
            onClick={() => setShowModal({ show: false, title: '', body: null })}
          >
            Cerrar
          </button>
        </div>
      </Backdrop>
    )
  }

  const icons = [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
    >
      XLS
    </button>
  ]

  return (
    <>
      <HeadingTitle title='Libro Diario' rightIcons={icons} iconHeight={32} />

      <div className='space-y-4'>
        <div className='flex flex-col gap-3'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end'>
            <div className='flex flex-col relative w-full'>
            <label>Fecha Desde</label>
            <input
              type='date'
              value={queryFrom}
              onChange={handleFromChange}
              max={queryTo}
              className='w-full border border-border rounded-full px-3 py-1.5 text-sm capitalize'
            />
          </div>
            <div className='flex flex-col relative w-full'>
            <label>Fecha Hasta</label>
            <input
              type='date'
              value={queryTo}
              onChange={handleToChange}
              min={queryFrom}
              className='w-full border border-gray-300 rounded-full px-3 py-1.5 text-sm capitalize'
            />
          </div>
          {/* Botón Buscar (usa queryFrom / queryTo) */}
          <CustomButton
              className='rounded-full h-8 w-full'
            color='primary'
            title='Buscar'
            onClick={handleSearch}
          />
        </div>

          <div className='flex justify-end gap-2'>
            {/* Botón para toggle de filtros avanzados */}
            <button
              className='flex items-center px-2 py-1 bg-surface/70 border border-border rounded hover:bg-border/30 transition'
              onClick={() => setShowFilters(prev => !prev)}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', {
                  'rotate-180': showFilters
                })}
              />
            </button>
            <InfoOutlinedIcon
              key='info'
              className='cursor-pointer text-muted hover:text-text'
              onClick={() => setInfoOpen(true)}
            />
          </div>
        </div>

        {showFilters && (
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 items-end border border-border rounded-md p-4 bg-surface/70'>
            {/* A) Filtro de Tipo */}
            <div className='flex flex-col w-full'>
              <select
                value={tipoFilter}
                onChange={e => setTipoFilter(e.target.value)}
                className='w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
              >
                <option value=''>Todos</option>
                <option value='Ingreso'>Ingreso</option>
                <option value='Egreso'>Egreso</option>
                <option value='Traspaso'>Traspaso</option>
              </select>
            </div>

            {/* B) Filtro de Comprobante */}
            <div className='flex flex-col w-full'>
              <input
                type='text'
                value={comprobanteFilter}
                onChange={e => setComprobanteFilter(e.target.value)}
                placeholder='Comprobante'
                className='w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
              />
            </div>

            {/* C) Filtro de RUT o Razón Social */}
            <div className='flex flex-col w-full'>
              <input
                type='text'
                value={clienteFilter}
                onChange={e => setClienteFilter(e.target.value)}
                placeholder='Busca RUT o Razón Social'
                className='w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
              />
            </div>
          </div>
        )}
        
        {isLoading ? (
          <div className='flex items-center justify-center pt-8 h-full'>
            <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <Table 
              rows={filteredData} 
              className="h-[40vh]"
              lastElementRef={lastElementRef}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
            />
          </div>
        )}
        
        <Modal />
        <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
        <ExportToXlsxModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
          title='Exportar Libro Diario'
          description='Se enviará un archivo Excel con el libro diario al email especificado'
          isLoading={isExporting}
        />
      </div>
    </>
  )
}

export default ListLibroDiario
